import React, {useEffect, useState} from "react";
import ShippingForm, {IShippingFormData} from "./ShippingForm";
import {useTranslation} from "react-i18next";
import {captureException} from "@sentry/browser";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import Spinner from "../../../utils/loader/Spinner";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {isAmazonLoggedIn} from "../../../lib/payments/AmazonPayLibrary";
import {ICheckoutPutParams} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import {isSameAddress} from "../AddressStep/AddressStep";
import {CheckoutError, DEFAULT_ERROR_MESSAGE} from "../../Checkout";
import window from "../../../lib/WindowExtended";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {ApiError} from "@connox/typescript-library";
import {PossibleNetworkError} from "../../../cart/ShoppingCart/ShoppingCart";
import {INTERNAL_ERROR_TYPE} from "../../../lib/payments/PaymentController";

const Shipping: React.FC<{isVisible: boolean}> = ({isVisible}) => {
  const {i18n, t} = useTranslation("checkout");
  const [phoneError, setPhoneError] = useState<string>("");
  const {gotoStep, shippingOptions} = useCheckoutViewContext();
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shippingData, setShippingData] = useState<IShippingFormData>();
  const {checkout} = useCheckoutContext();
  const {deliveryAddress, invoiceAddress} = checkout;

  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
      setErrors([]);
      setPhoneError("");
      window.isIntoSettingPhoneNoInShippingStep = false;
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const submitData = async () => {
    const {shippingOption, phone, shippingDate, isPartDeliveryAllowed} = shippingData as IShippingFormData;
    const {isPhoneRequired, logisticsTypeId} = shippingOption;

    if (isPhoneRequired && !isAmazonLoggedIn() && !phone) {
      setPhoneError("Bitte ausfüllen");

      return;
    }

    const wasBillingPaymentSet = window.isBillingAgreementPaymentSet;
    const wasPreAuthDone = window.isPreauthorizingDone;
    window.isBillingAgreementPaymentSet = window.isPreauthorizingDone = false;
    window.isBillingAgreementPaymentSet = window.isPreauthorizingDone = false;
    setIsLoading(true);

    let shippingParams: ICheckoutPutParams = {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      favouredDateShipping: shippingDate,
      isPartDeliveryAllowed,
      logisticsTypeId,
    };

    if (deliveryAddress && phone && isPhoneRequired && !isAmazonLoggedIn()) {
      if (isSameAddress(deliveryAddress, invoiceAddress)) {
        shippingParams = {
          ...shippingParams,
          address: {
            delivery: {...deliveryAddress, phone},
            invoice: {...deliveryAddress, phone},
          },
        };
      } else {
        shippingParams = {
          ...shippingParams,
          address: {
            delivery: {...deliveryAddress, phone},
          },
        };
      }
    }
    if (phone !== deliveryAddress?.phone) {
      window.isIntoSettingPhoneNoInShippingStep = true;
    }
    if (
      (shippingParams.favouredDateShipping ?? null) === checkout.favouredDateShipping &&
      shippingParams.isPartDeliveryAllowed === checkout.isPartDeliveryAllowed &&
      (shippingParams.logisticsTypeId ?? null) === checkout.logisticsTypeId &&
      phone === deliveryAddress?.phone
    ) {
      window.isBillingAgreementPaymentSet = wasBillingPaymentSet;
      window.isPreauthorizingDone = wasPreAuthDone;
      gotoStep();

      return;
    }

    try {
      await checkout.put(shippingParams);
      setErrors([]);
      setIsLoading(false);
    } catch (err) {
      setErrors([]);
      setPhoneError("");
      setIsLoading(false);
      const tempError = ((err as ApiError).originalError || err) as PossibleNetworkError;
      const result = tempError.response?.data.result?.code || tempError.response?.data.result;
      const errMessage: string = result || tempError.message;

      let sentryEventId: string | undefined;
      if (!errMessage || !i18n.exists(errMessage)) {
        tempError.name = INTERNAL_ERROR_TYPE.SHIPPING.OnCatch + tempError.name;
        sentryEventId = captureException(tempError, {extra: {place: "Shipping after checkout put", errMessage}});
      }
      if (errMessage.toLowerCase().includes("phone")) {
        setPhoneError(errMessage);
      } else {
        setErrors([t(errMessage, t(DEFAULT_ERROR_MESSAGE, {sentryEventId}))]);
      }
    }
  };

  return (
    <div className="Shipping Mobile-Only-Friendly">
      <h1>{t("Versandoptionen")}</h1>
      <CheckoutError>{errors}</CheckoutError>
      <ShippingForm
        phoneError={phoneError}
        onChange={(data) => {
          setShippingData(data);
        }}
      />
      <CartDetails
        reduced
        selectedShippingOption={shippingOptions.find(
          (option) => option.logisticsTypeId === shippingData?.shippingOption.logisticsTypeId,
        )}
      />
      <Button
        action={(evt) => {
          evt.preventDefault();
          void submitData();
        }}
        styles={[ButtonStyles.wide]}
        type={isLoading ? ButtonType.disabled : ButtonType.default}
      >
        {isLoading ? <Spinner /> : <span>{t("Weiter")}</span>}
      </Button>
    </div>
  );
};

export default Shipping;
